// @story HIR-SYS3
// @story HIR-HR28
// @story HIR-HR29
// What follows a written test on its stage tab (server:
// services/responseSheet.service.js, strongSpecialist.service.js,
// demoReferral.service.js):
//   • Response sheet — the school's own form responses, pasted or uploaded
//     as CSV, matched to the right people (registration number, then name;
//     the email only breaks a tie). The rows a person must decide sit below.
//   • Strong subject, not selected — below the pass mark overall, strong in
//     their own subject; "Select this candidate" with a reason.
//   • Send to a demo panellist — those who cleared the written test, to a
//     named subject head each.
// Every change is shown first (the server's preview) and made only on
// Confirm. Nothing here messages a candidate.
import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { Button, Card, CardHeader, Select, StatusPill } from '../../ds';

const REASON_MIN = 12;
const small = { fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' };
const field = { width: '100%', border: '1px solid var(--border, #ddd)', borderRadius: 6, padding: '4px 6px', font: 'inherit', fontSize: 'var(--fs-body-sm)' };
const box = { display: 'grid', gap: 6, padding: 10, borderRadius: 8, background: 'var(--surface-2, #f6f6f6)' };
const errText = (e, fallback) => e?.response?.data?.message || fallback;
const errLine = (t) => (t ? <span role="alert" style={{ ...small, color: 'var(--status-danger-text)' }}>{t}</span> : null);

/* Preview first, then Confirm. */
function useFlow(onDone) {
  const [shown, setShown] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const run = async (url, body, preview) => {
    setBusy(true); setErr('');
    try {
      const res = await api.post(url, { ...body, preview });
      if (preview) setShown({ url, body, data: res.data?.data || {} });
      else { setShown(null); if (onDone) onDone(res.data?.data); }
    } catch (e) {
      setErr(errText(e, 'Could not do that just now. Nothing was changed.'));
    } finally { setBusy(false); }
  };
  return { shown, busy, err, preview: (url, body) => run(url, body, true), confirm: () => run(shown.url, shown.body, false), cancel: () => setShown(null) };
}

function ResponseSheet({ jobId, stageId, onChanged }) {
  const [csv, setCsv] = useState('');
  const [done, setDone] = useState('');
  const flow = useFlow((d) => { setCsv(''); setDone(`Imported: ${d.counts.link} linked, ${d.counts.review + d.counts.unmatched} for a person to decide.`); onChanged(); });
  const url = `/written-test/${jobId}/${stageId}/response-sheet`;
  const readFile = (f) => { if (!f) return; const r = new FileReader(); r.onload = () => { setCsv(String(r.result || '')); flow.cancel(); }; r.readAsText(f); };
  const c = flow.shown?.data?.counts;
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <span style={small}>The paper sat on the school's own form: download its responses as CSV and add them here. Each response is matched by registration number, then by name; the email only breaks a tie. Nothing is marked until the answer key is confirmed.</span>
      <input type="file" accept=".csv,text/csv" aria-label="Response sheet file" onChange={(e) => readFile(e.target.files?.[0])} />
      <textarea aria-label="Response sheet CSV" rows={3} style={field} value={csv} placeholder="…or paste the CSV here, header row first" onChange={(e) => { setCsv(e.target.value); flow.cancel(); }} />
      {!flow.shown ? (
        <div><Button size="sm" disabled={flow.busy || !csv.trim()} onClick={() => flow.preview(url, { csv })}>Check before importing</Button></div>
      ) : (
        <div style={box}>
          <span>
            {c.responses} responses: <strong>{c.link} linked</strong>{c.flagged ? ` (${c.flagged} flagged for a check)` : ''},
            {' '}{c.review + c.unmatched} for a person to decide{c.already ? `, ${c.already} imported before` : ''}.
            {!flow.shown.data.paper?.keyConfirmed && ' The answer key is not confirmed yet, so nobody is marked until it is.'}
          </span>
          <table style={{ fontSize: 'var(--fs-caption)', borderCollapse: 'collapse' }}>
            <tbody>
              {(flow.shown.data.rows || []).slice(0, 100).map((r) => (
                <tr key={r.sheetRow}>
                  <td style={{ padding: '2px 6px' }}>Row {r.sheetRow}</td>
                  <td style={{ padding: '2px 6px' }}>{r.name || r.registration || '—'}</td>
                  <td style={{ padding: '2px 6px' }}>
                    {r.status === 'linked'
                      ? <>→ {r.candidate.name}{r.flags.length ? <span style={{ color: 'var(--status-warning-text)' }}> · {r.flags.join(' ')}</span> : null}{r.alsoFor.length ? ` · also on ${r.alsoFor.length} other application${r.alsoFor.length === 1 ? '' : 's'}` : ''}</>
                      : <span style={{ color: 'var(--text-2)' }}>{r.status}: {r.why}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: 6 }}>
            <Button size="sm" disabled={flow.busy} onClick={flow.confirm}>Import</Button>
            <Button size="sm" variant="ghost" disabled={flow.busy} onClick={flow.cancel}>Cancel</Button>
          </div>
        </div>
      )}
      {done && <span style={small}>{done}</span>}
      {errLine(flow.err)}
    </div>
  );
}

function ReviewQueue({ jobId, stageId, version, onChanged }) {
  const [queue, setQueue] = useState(null);
  const [pick, setPick] = useState({});
  const [reason, setReason] = useState({});
  const load = useCallback(() => {
    api.get(`/written-test/${jobId}/${stageId}/responses/review`).then((r) => setQueue(r.data?.data || null)).catch(() => setQueue(null));
  }, [jobId, stageId]);
  useEffect(() => { load(); }, [load, version]);
  const flow = useFlow(() => { load(); onChanged(); });
  if (!queue || !queue.waiting) return null;
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <strong style={{ fontSize: 'var(--fs-body-sm)' }}>{queue.waiting} response{queue.waiting === 1 ? '' : 's'} for a person to decide</strong>
      {queue.responses.map((r) => {
        const body = { reason: (reason[r.responseId] || '').trim(), ...(pick[r.responseId] === 'dismiss' ? { dismiss: true } : { resumeId: pick[r.responseId] }) };
        const ok = pick[r.responseId] && body.reason.length >= REASON_MIN;
        return (
          <div key={r.responseId} style={{ ...box, background: 'transparent', border: '1px solid var(--border)' }}>
            <span style={{ fontSize: 'var(--fs-body-sm)' }}>
              <strong>{r.identity.name || r.identity.registration || 'No name'}</strong>
              {r.identity.email ? <span style={small}> · {r.identity.email}</span> : null} — {r.why}
            </span>
            <Select value={pick[r.responseId] || ''} placeholder="Whose is it?" onChange={(v) => { setPick((p) => ({ ...p, [r.responseId]: v })); flow.cancel(); }}
              options={[...r.suggestions.map((s) => ({ value: s.resumeId, label: s.name })), { value: 'dismiss', label: "Nobody's here (dismiss)" }]} />
            <input aria-label="Reason" style={field} value={reason[r.responseId] || ''} placeholder={`Why (at least ${REASON_MIN} characters)`} onChange={(e) => setReason((x) => ({ ...x, [r.responseId]: e.target.value }))} />
            {flow.shown?.body && flow.shown.url.includes(r.responseId) ? (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={small}>{flow.shown.data.decision === 'dismiss' ? 'Dismiss this response.' : `Attach it to ${flow.shown.data.candidate?.name}.`}</span>
                <Button size="sm" disabled={flow.busy} onClick={flow.confirm}>Confirm</Button>
                <Button size="sm" variant="ghost" onClick={flow.cancel}>Cancel</Button>
              </div>
            ) : (
              <div><Button size="sm" variant="secondary" disabled={!ok || flow.busy} onClick={() => flow.preview(`/written-test/responses/${r.responseId}/resolve`, body)}>Check</Button></div>
            )}
          </div>
        );
      })}
      {errLine(flow.err)}
    </div>
  );
}

function StrongSpecialists({ jobId, stageId, version, onChanged }) {
  const [list, setList] = useState(null);
  const [reason, setReason] = useState({});
  const load = useCallback(() => {
    api.get(`/written-test/${jobId}/${stageId}/strong-specialists`).then((r) => setList(r.data?.data || null)).catch(() => setList(null));
  }, [jobId, stageId]);
  useEffect(() => { load(); }, [load, version]);
  const flow = useFlow(() => { load(); onChanged(); });
  if (!list || !list.listed) return null;
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <strong style={{ fontSize: 'var(--fs-body-sm)' }}>Strong subject, not selected</strong>
      <span style={small}>Below the {list.passMark}% pass mark on the whole paper, but {list.strongThreshold}% or more in their own subject. Nobody is selected unless a person does it here, with a reason.</span>
      {list.candidates.map((c) => {
        const why = (reason[c.candidateId] || '').trim();
        const url = `/written-test/${c.candidateId}/stages/${stageId}/select-specialist`;
        return (
          <div key={c.candidateId} style={{ ...box, background: 'transparent', border: '1px solid var(--border)' }}>
            <span style={{ fontSize: 'var(--fs-body-sm)' }}>
              <strong>{c.name}</strong> <StatusPill tone="warning">{c.overall}% overall</StatusPill> <StatusPill tone="success">{c.subject}% subject</StatusPill>
            </span>
            <span style={small}>{c.reason} Selected by {c.selectBy}.</span>
            <input aria-label={`Reason for ${c.name}`} style={field} value={reason[c.candidateId] || ''} placeholder={`Why select them (at least ${REASON_MIN} characters)`} onChange={(e) => setReason((x) => ({ ...x, [c.candidateId]: e.target.value }))} />
            {flow.shown?.url === url ? (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={small}>Select {c.name} by {flow.shown.data.how}. The candidate is not told.</span>
                <Button size="sm" disabled={flow.busy} onClick={flow.confirm}>Confirm</Button>
                <Button size="sm" variant="ghost" onClick={flow.cancel}>Cancel</Button>
              </div>
            ) : (
              <div><Button size="sm" variant="secondary" disabled={why.length < REASON_MIN || flow.busy} onClick={() => flow.preview(url, { reason: why })}>Select this candidate</Button></div>
            )}
          </div>
        );
      })}
      {errLine(flow.err)}
    </div>
  );
}

function DemoReferral({ jobId, stageId, demoStages, version, onChanged }) {
  const [demoStageId, setDemo] = useState(demoStages[0]?.stageId || '');
  const [data, setData] = useState(null);
  const [people, setPeople] = useState([]);
  const [chosen, setChosen] = useState({});
  const [to, setTo] = useState('');
  const load = useCallback(() => {
    if (!demoStageId) return;
    api.get(`/written-test/${jobId}/demo-referrals`, { params: { fromStageId: stageId, demoStageId } }).then((r) => setData(r.data?.data || null)).catch(() => setData(null));
  }, [jobId, stageId, demoStageId]);
  useEffect(() => { load(); }, [load, version]);
  useEffect(() => { api.get('/human-interview/panel-people').then((r) => setPeople(r.data?.data || [])).catch(() => {}); }, []);
  const flow = useFlow(() => { setChosen({}); load(); onChanged(); });
  if (!demoStages.length) return null;
  const ids = Object.keys(chosen).filter((k) => chosen[k]);
  const url = `/written-test/${jobId}/demo-referrals`;
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <strong style={{ fontSize: 'var(--fs-body-sm)' }}>Send to a demo panellist</strong>
      <span style={small}>Only those who cleared this test (a pass mark, the exit filter's return, or HR's pass) can be sent. Each lands in that panellist's own list.</span>
      {demoStages.length > 1 && (
        <Select label="Demo stage" value={demoStageId} onChange={setDemo} options={demoStages.map((s) => ({ value: s.stageId, label: s.label }))} />
      )}
      {!data || !data.cleared ? <span style={small}>Nobody has cleared this test yet.</span> : (
        <>
          {data.candidates.map((c) => (
            <label key={c.candidateId} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 'var(--fs-body-sm)' }}>
              <input type="checkbox" checked={Boolean(chosen[c.candidateId])} onChange={(e) => { setChosen((x) => ({ ...x, [c.candidateId]: e.target.checked })); flow.cancel(); }} />
              {c.name} <span style={small}>({c.cleared}){c.sentTo ? ` · sent to ${c.sentTo}` : ''}</span>
            </label>
          ))}
          <Select label="Subject head taking the demo" value={to} onChange={(v) => { setTo(v); flow.cancel(); }} placeholder="Choose a person…"
            options={people.map((p) => ({ value: p.userId, label: p.label || `${p.name} (${p.email})` }))} />
          {flow.shown ? (
            <div style={box}>
              <span>Send {flow.shown.data.sends.map((s) => s.name).join(', ')} to {flow.shown.data.sends[0]?.to}. Nobody is messaged.</span>
              {flow.shown.data.sends.some((s) => s.warning) && <span style={{ ...small, color: 'var(--status-warning-text)' }}>{flow.shown.data.sends.find((s) => s.warning).warning}</span>}
              <div style={{ display: 'flex', gap: 6 }}>
                <Button size="sm" disabled={flow.busy} onClick={flow.confirm}>Send</Button>
                <Button size="sm" variant="ghost" onClick={flow.cancel}>Cancel</Button>
              </div>
            </div>
          ) : (
            <div><Button size="sm" variant="secondary" disabled={!ids.length || !to || flow.busy} onClick={() => flow.preview(url, { fromStageId: stageId, demoStageId, candidateIds: ids, panellistUserId: to })}>Check before sending</Button></div>
          )}
        </>
      )}
      {errLine(flow.err)}
    </div>
  );
}

export default function WrittenFollowUp({ jobId, stage, demoStages = [], onChanged }) {
  const [version, setVersion] = useState(0);
  const changed = () => { setVersion((v) => v + 1); if (onChanged) onChanged(); };
  return (
    <Card padding="16px 20px">
      <CardHeader title="Response sheet and next steps" subtitle="Responses from the school's own form, strong subject scores, and the demo panellists" />
      <div style={{ display: 'grid', gap: 16 }}>
        <ResponseSheet jobId={jobId} stageId={stage.stageId} onChanged={changed} />
        <ReviewQueue jobId={jobId} stageId={stage.stageId} version={version} onChanged={changed} />
        <StrongSpecialists jobId={jobId} stageId={stage.stageId} version={version} onChanged={changed} />
        <DemoReferral jobId={jobId} stageId={stage.stageId} demoStages={demoStages} version={version} onChanged={changed} />
      </div>
    </Card>
  );
}
